/**
 * @deprecated use extractDynamicFieldOptions instead
 * Generates the payer list from the payers found in all coverage positions.
 *
 * @param  {Object} coverage coverage object from react state.
 * @param  {Object} settings settings object from react state.
 *
 * @return {Array} payers
 */
export const restrictPayers = (coverage, settings) => {
  let payers = coverage.get('payers').toJS();

  if (
    typeof settings.restrictToCoveragePositionPayers !== 'undefined'
    && settings.restrictToCoveragePositionPayers === true
  ) {
    // Get all unique payers found in the coverage positions.
    let hash = {};
    for (const row of coverage.get('coveragePositions').toJS()) {
      hash[row.payer] = row.payerid;
    }

    payers = [];
    for (const payer in hash) {
      payers.push({id: hash[payer], label: payer});
    }
  }

  return payers;
}

/**
 * @deprecated use extractDynamicFieldOptions instead
 */
export const extractPbms = (coveragePositions) => {
  let hash = {};
  for (const row of coveragePositions) {
    hash[row.pbm] = row.pbmid;
  }

  const pbms = Object.entries(hash)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => ({label: key, id: value}));
  return pbms;
}

/**
 * Creates a consistent key from fieldName (string or pipe-delimited string)
 * Supports formats: "payer" or "payer|pbmClient"
 * @param {String} fieldName - Field name(s) as string
 * @returns {String} Key like "payer" or "pbmClient|payer"
 */
export const getFieldNameKey = (fieldName) => {
  if (!fieldName) {
    return '';
  }

  // Handle pipe-delimited string or single string
  const fields = String(fieldName).split('|').map((f) => f.trim()).filter(Boolean);
  return fields.sort().join('|');
}

/**
 * Extracts unique filter options from coverage positions for a dynamic field
 * Supports formats: "payer" or "payer|pbmClient"
 * @param {Array} coveragePositions - Array of coverage positions
 * @param {String} fieldName - The fieldName(s) to extract values for (pipe-delimited string)
 * @returns {Array} Array of {id, label} objects for filter options
 */
export const extractDynamicFieldOptions = (coveragePositions, fieldName) => {
  const valueMap = new Map();

  // Normalize to array: handle pipe-delimited string or single string
  if (!fieldName) {
    return []; // Return empty array if fieldName is null/undefined/empty
  }

  const fieldNames = String(fieldName).split('|').map((f) => f.trim()).filter(Boolean);

  // Return empty array if no valid field names after normalization
  if (fieldNames.length === 0) {
    return [];
  }

  // Helper functions to process values
  const getValueKey = (value) => typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value);

  const getValueLabel = (value) => typeof value === 'object' && value !== null
    ? (value.label || value.name || String(value))
    : String(value);

  // Iterate through each coverage position to extract unique field values
  coveragePositions.forEach((position) => {
    // Skip positions that don't have a fields array
    if (!position.fields || !Array.isArray(position.fields)) {
      return;
    }

    // Check each field name (supports multiple fields via pipe-delimited string)
    fieldNames.forEach((name) => {
      // Find the field matching the current field name
      const field = position.fields.find((f) => f.fieldName === name);
      // Skip if field not found or has no value
      if (!field?.value) {
        return;
      }

      // Normalize field value to array (handles both single values and arrays)
      const values = Array.isArray(field.value) ? field.value : [field.value];
      // Process each value in the field
      values.forEach((value) => {
        const key = getValueKey(value);
        // Add to map if not already present (ensures uniqueness)
        if (!valueMap.has(key)) {
          valueMap.set(key, {id: key, label: getValueLabel(value), value});
        }
      });
    });
  });

  return Array.from(valueMap.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}

/**
 * Checks if a position matches any of the given fieldNames with selectedValues
 * @param {Object} position - Coverage position
 * @param {Array<String>} fieldNames - Field names to check
 * @param {Array} selectedValues - Selected filter values
 * @returns {Boolean} True if position matches
 */
const matchesFieldValue = (position, fieldNames, selectedValues) => {
  // Skip positions that don't have a fields array
  if (!position.fields || !Array.isArray(position.fields)) {
    return false;
  }

  // Check if any field name matches
  return fieldNames.some((fieldName) => {
    // Find the field matching the current field name
    const field = position.fields.find((f) => f.fieldName === fieldName);
    // Skip if field not found or has no value
    if (!field || field.value === undefined || field.value === null) {
      return false;
    }

    // Normalize field value to array and convert to keys for comparison
    const fieldValues = Array.isArray(field.value) ? field.value : [field.value];
    const fieldValueKeys = fieldValues.map((v) =>
      typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
    );

    // Check if any selected value matches the field values
    return selectedValues.some((selected) => {
      // Extract key from selected value (prefer id if object, otherwise stringify)
      const selectedKey = typeof selected === 'object' && selected.id !== undefined
        ? selected.id
        : String(selected);
      return fieldValueKeys.includes(selectedKey);
    });
  });
}

/**
 * Applies dynamic column filters to filtered positions
 * Handles fieldNameKey as pipe-delimited string (e.g., "payer|pbmClient")
 * @param {Array} filteredPositions - Already filtered positions
 * @param {Object} selectedDynamicFilters - {fieldNameKey: [selectedValues]}
 * @param {Object} settings - Settings object
 * @returns {Array} Filtered positions
 */
export const applyDynamicFilters = (filteredPositions, selectedDynamicFilters, settings) => {
  if (!settings.table.dynamicColumns || !selectedDynamicFilters || Object.keys(selectedDynamicFilters).length === 0) {
    return filteredPositions;
  }

  return filteredPositions.filter((position) => {
    return Object.entries(selectedDynamicFilters).every(([fieldNameKey, selectedValues]) => {
      if (!selectedValues || selectedValues.length === 0) {
        return true;
      }

      // Parse key: split if contains '|', otherwise single field
      // Supports keys like "payer" or "payer|pbmClient"
      const fieldNames = fieldNameKey.includes('|')
        ? fieldNameKey.split('|')
        : [fieldNameKey];

      return matchesFieldValue(position, fieldNames, selectedValues);
    });
  });
}

